// Report Templates (/radiology-report-templates) — full CRUD over the report
// layouts a hospital authors for itself.
//
// A template is just a name + matching hints + a blob of HTML written in the
// rich text editor. The frontend never inspects that HTML: headings, tables,
// organ-wise lists, letterheads, {{placeholders}} — all of it is the hospital's
// business. That is what keeps this module free of hardcoded report fields.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import showConfirmDialog from "../../components/ShowConfirmDialog/ShowConfirmDialog";
import ClinicalEditor from "../../components/ClinicalEditor/ClinicalEditor";
import RadiologyLayout from "./RadiologyLayout";
import ReportContent from "./components/ReportContent";
import SvgIcon from "../ipd-billing/SvgIcon";
import { C } from "../ipd-billing/constants";
import { Btn, Modal, PageHeader, cardStyle, inputStyle, labelStyle } from "../rule-builder/ui";
import { searchChargeCaptureServices } from "../ipd-billing/billing-api";
import { MODALITY_OPTIONS } from "./radiologyOrdersShared";
import useRadiologyTemplates from "./hooks/useRadiologyTemplates";
import {
  markTemplateSuperseded,
  registerTemplateService,
  saveRadiologyReportTemplate,
  writeTemplateMeta,
} from "./api/radiologyReportApi";
import { PLACEHOLDERS, htmlIsEmpty } from "./utils/radiologyPlaceholders";

const SERVICE_SEARCH_DEBOUNCE_MS = 300;

const emptyDraft = () => ({
  template_id: "",
  template_name: "",
  modality: "",
  // One investigation, exactly as save_radiology_report_template/ models it:
  // { name, medical_service_id } or null. service_names stays derived from it
  // for everything downstream (matching, list captions) that wants the label.
  service_ref: null,
  service_names: [],
  description: "",
  content_html: "",
  is_active: true,
});

// The one investigation a draft is filed under, or "" when none is chosen.
const draftServiceId = (draft) => draft?.service_ref?.medical_service_id || "";

// Re-editing an existing template: normalizeTemplate hands back a service_refs
// array (older templates carry names with no ids). Take the first entry that
// actually has an id — anything else can't be uploaded, so it must be re-picked.
const toServiceRef = (template) =>
  (template?.service_refs || []).find((r) => r.medical_service_id) || null;

// The single investigation a template is authored for — same search users
// already know from Care Plan and Order Management (GET
// ipd/billing/charge-capture/services/?group_code=RADIOLOGY).
//
// Selection only, no free text: save_radiology_report_template/ is keyed by
// medical_service_id, and a hand-typed name has none, so it could never be
// uploaded. Letting one be entered only produced a template that silently
// belonged to nothing.
function ServicePicker({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | loaded | error
  const timerRef = useRef(null);
  const seqRef = useRef(0);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const runSearch = (text) => {
    clearTimeout(timerRef.current);
    const trimmed = text.trim();
    if (trimmed.length < 2) {
      setOptions([]);
      setStatus("idle");
      return;
    }
    setStatus("loading");
    timerRef.current = setTimeout(() => {
      const seq = ++seqRef.current;
      searchChargeCaptureServices(trimmed, { limit: 50, groupCode: "RADIOLOGY" })
        .then((rows) => {
          if (seq !== seqRef.current) return;
          // charge-capture returns one row per bed-type tariff, so a single
          // investigation comes back several times ("Chest X-Ray" ×5). A
          // template matches on the name alone — collapse to distinct names,
          // keeping the first row's medical_service_id for the upload.
          const seen = new Set();
          const picked = [];
          rows.forEach((r) => {
            const name = String(r?.name || "").trim();
            if (!name || name === "—") return;
            const key = name.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            picked.push({
              name,
              medical_service_id: String(r?.medical_service_id ?? r?.id ?? ""),
              medical_service_no: String(
                r?._raw?.medical_service_no ??
                  r?._raw?.emr_medical_service_id ??
                  "",
              ),
            });
          });
          setOptions(picked);
          setStatus("loaded");
        })
        .catch(() => {
          if (seq !== seqRef.current) return;
          setOptions([]);
          setStatus("error");
        });
    }, SERVICE_SEARCH_DEBOUNCE_MS);
  };

  // `item` is a picked option ({name, medical_service_id}) or a raw typed
  // string — the latter has no id, so it can label a template but not scope it.
  const add = (item) => {
    const name = String(item?.name || "").trim();
    const medical_service_id = String(item?.medical_service_id || "");
    if (!name || !medical_service_id) return;
    onChange({ name, medical_service_id });
    setQuery("");
    setOptions([]);
    setStatus("idle");
  };

  const clear = () => {
    onChange(null);
    setQuery("");
    setOptions([]);
    setStatus("idle");
  };

  // Chosen → show what was chosen, with a way out. The search box only exists
  // while there is nothing selected, which is what makes this single-select.
  if (value) {
    return (
      <div
        style={{
          ...inputStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value.name}
        </span>
        <button
          type="button"
          onClick={clear}
          title="Change investigation"
          style={{
            border: "none",
            background: "none",
            cursor: "pointer",
            padding: 0,
            display: "flex",
            flexShrink: 0,
            color: C.textSub,
          }}
        >
          <SvgIcon name="x" size={12} color={C.textSub} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <input
        style={inputStyle}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          runSearch(e.target.value);
        }}
        placeholder="Search radiology investigations — type 2+ letters"
      />
      {status === "loading" || options.length || status === "error" ? (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "calc(100% + 2px)",
            zIndex: 20,
            maxHeight: 220,
            overflowY: "auto",
            background: "#fff",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(13,35,63,0.14)",
            padding: 4,
          }}
        >
          {status === "loading" ? (
            <div style={{ padding: "7px 10px", fontSize: 12, color: C.textSub }}>Searching…</div>
          ) : status === "error" ? (
            <div style={{ padding: "7px 10px", fontSize: 12, color: C.textSub }}>
              Service search unavailable — try again in a moment.
            </div>
          ) : (
            options.map((opt) => (
              <button
                key={`${opt.name}-${opt.medical_service_id}`}
                type="button"
                onClick={() => add(opt)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "7px 10px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 12,
                  color: C.text,
                  borderRadius: 6,
                }}
              >
                {opt.name}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function TemplateRow({ template, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "10px 12px",
        borderRadius: 8,
        border: `1.5px solid ${active ? C.primary : "transparent"}`,
        background: active ? "#EEF4FF" : "transparent",
        cursor: "pointer",
        fontFamily: "inherit",
        marginBottom: 4,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: C.text,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {template.template_name}
        </span>
        {template.is_active === false ? (
          <span style={{ fontSize: 9, fontWeight: 700, color: "#B91C1C" }}>INACTIVE</span>
        ) : null}
      </div>
      {/* One investigation per template, so name it rather than counting. */}
      <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>
        {template.modality || "Any modality"}
        {(template.service_names || []).length
          ? ` · ${template.service_names.join(", ")}`
          : ""}
      </div>
    </button>
  );
}

// Inserts a {{token}} at the caret. ClinicalEditor exposes the TipTap instance
// through its ref, so nothing here needs to know about editor internals.
function PlaceholderMenu({ editorRef }) {
  const [open, setOpen] = useState(false);

  const insert = (token) => {
    const editor = editorRef.current?.getEditor?.();
    if (!editor) return;
    editor.chain().focus().insertContent(`{{${token}}}`).run();
    setOpen(false);
  };

  return (
    <div style={{ position: "relative" }}>
      <Btn variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>
        <SvgIcon name="plus" size={13} color={C.text} />
        Insert placeholder
      </Btn>
      {open ? (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 4px)",
            zIndex: 20,
            width: 230,
            maxHeight: 260,
            overflowY: "auto",
            background: "#fff",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(13,35,63,0.14)",
            padding: 4,
          }}
        >
          {PLACEHOLDERS.map((p) => (
            <button
              key={p.token}
              type="button"
              onClick={() => insert(p.token)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "7px 10px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 12,
                color: C.text,
                borderRadius: 6,
              }}
            >
              {p.label}
              <div style={{ fontSize: 10, color: C.textSub }}>{`{{${p.token}}}`}</div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PreviewModal({ template, onClose }) {
  return (
    <Modal
      open
      onClose={onClose}
      width={900}
      title={template.template_name || "Untitled template"}
      description="Rendered exactly as the radiologist will receive it"
      footer={<Btn onClick={onClose}>Close</Btn>}
    >
      <ReportContent
        html={template.content_html}
        emptyText="Empty template."
        style={{
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          padding: 24,
          background: "#fff",
        }}
      />
    </Modal>
  );
}

export default function RadiologyTemplates() {
  const {
    templates,
    loading,
    error,
    hydratingId,
    reload,
    hydrateTemplateContent,
  } = useRadiologyTemplates({ catalog: true });
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState(null); // non-null ⇒ edit mode
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) =>
      `${t.template_name} ${t.modality} ${(t.service_names || []).join(" ")}`
        .toLowerCase()
        .includes(q),
    );
  }, [templates, search]);

  const selected = templates.find((t) => t.template_id === selectedId) || null;
  const editing = draft;

  const guardDirty = () =>
    !draft || showConfirmDialog("Discard unsaved template changes?");

  const handleSelect = async (id) => {
    if (!(await guardDirty())) return;
    setDraft(null);
    setSelectedId(id);
    const row = templates.find((t) => t.template_id === id);
    if (row) await hydrateTemplateContent(row);
  };

  const handleNew = async () => {
    if (!(await guardDirty())) return;
    setDraft(emptyDraft());
    setSelectedId("");
  };

  const handleEdit = async () => {
    if (!selected) return;
    const hydrated = await hydrateTemplateContent(selected);
    setDraft({
      ...hydrated,
      service_ref: toServiceRef(hydrated),
    });
  };

  const handleDuplicate = async () => {
    if (!selected) return;
    const hydrated = await hydrateTemplateContent(selected);
    setDraft({
      ...hydrated,
      template_id: "",
      template_name: `${hydrated.template_name} (copy)`,
      service_ref: toServiceRef(hydrated),
    });
  };

  const handleSave = async () => {
    if (!draft.template_name.trim()) {
      toast.error("Give the template a name.");
      return;
    }
    const serviceId = draftServiceId(draft);
    if (!serviceId) {
      toast.error("Select the investigation this template belongs to.");
      return;
    }
    // The editor is the source of truth for what the author is looking at; its
    // onChange can lag the final keystroke, so read the HTML off it directly.
    const html = editorRef.current?.getHTML?.() || draft.content_html;
    if (htmlIsEmpty(html)) {
      toast.error("The template body is empty.");
      return;
    }

    const editingId = String(draft.template_id || "").trim();
    setUploading(true);
    try {
      const uploadRes = await saveRadiologyReportTemplate({
        medicalServiceId: serviceId,
        contentHtml: html,
        templateName: draft.template_name.trim(),
        templateId: editingId || undefined,
      });
      registerTemplateService({
        medical_service_id: serviceId,
        medical_service_no: draft.service_ref.medical_service_no,
        name: draft.service_ref.name,
        id_aliases: [
          serviceId,
          draft.service_ref.medical_service_no,
        ].filter(Boolean),
      });
      const savedId = String(uploadRes?.template_id || editingId).trim();
      if (editingId && savedId && savedId !== editingId) {
        markTemplateSuperseded(editingId, savedId);
      }
      if (savedId) {
        writeTemplateMeta(savedId, {
          template_name: draft.template_name.trim(),
          modality: draft.modality,
          description: draft.description,
          is_active: draft.is_active,
          service_names: [draft.service_ref.name],
        });
      }
      await reload();
      if (savedId) setSelectedId(savedId);
      setDraft(null);
      toast.success(editingId ? "Template updated." : "Template saved.");
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Could not upload the template.",
      );
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (template) => {
    const hydrated = await hydrateTemplateContent(template);
    setPreview(hydrated);
  };

  const setField = (key) => (e) => setDraft({ ...draft, [key]: e.target.value });

  return (
    <RadiologyLayout>
      <div style={{ padding: "28px 32px" }}>
        <PageHeader
          title="Report Templates"
          subtitle="Author the report format your hospital uses — any layout, tables and placeholders included"
          actions={
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <Btn onClick={handleNew}>
                <SvgIcon name="plus" size={14} color="#fff" />
                New template
              </Btn>
            </div>
          }
        />

        {error ? (
          <div
            style={{
              ...cardStyle,
              padding: "10px 14px",
              marginBottom: 14,
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              color: "#991B1B",
              fontSize: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {error}
            <Btn variant="outline" size="sm" onClick={reload}>
              Retry
            </Btn>
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
          {/* ── Template list ─────────────────────────────────────────── */}
          <div style={{ ...cardStyle, padding: 12, width: 290, flexShrink: 0 }}>
            <input
              style={{ ...inputStyle, marginBottom: 10 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates…"
            />
            {loading ? (
              <div style={{ padding: 16, fontSize: 12, color: C.textSub }}>Loading…</div>
            ) : !filtered.length ? (
              <div style={{ padding: 16, fontSize: 12, color: C.textSub }}>
                {templates.length
                  ? "No template matches that search."
                  : "No templates yet — create your first one."}
              </div>
            ) : (
              filtered.map((t) => (
                <TemplateRow
                  key={t.template_id}
                  template={t}
                  active={!draft && selectedId === t.template_id}
                  onSelect={() => handleSelect(t.template_id)}
                />
              ))
            )}
          </div>

          {/* ── Editor / viewer ───────────────────────────────────────── */}
          <div style={{ ...cardStyle, padding: 18, flex: 1, minWidth: 0 }}>
            {editing ? (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>
                    {draft.template_id ? "Edit template" : "New template"}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <PlaceholderMenu editorRef={editorRef} />
                    <Btn variant="outline" size="sm" onClick={() => setDraft(null)}>
                      Cancel
                    </Btn>
                    <Btn size="sm" onClick={handleSave} disabled={uploading}>
                      <SvgIcon name="check" size={13} color="#fff" />
                      {uploading
                        ? "Uploading…"
                        : draft.template_id
                          ? "Update template"
                          : "Save template"}
                    </Btn>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr",
                    gap: 12,
                    marginBottom: 14,
                  }}
                >
                  <div>
                    <label style={labelStyle}>Template name *</label>
                    <input
                      style={inputStyle}
                      value={draft.template_name}
                      onChange={setField("template_name")}
                      placeholder="e.g. MRI Brain — Plain"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Modality</label>
                    <select style={inputStyle} value={draft.modality} onChange={setField("modality")}>
                      <option value="">Any modality</option>
                      {MODALITY_OPTIONS.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Status</label>
                    <select
                      style={inputStyle}
                      value={draft.is_active ? "active" : "inactive"}
                      onChange={(e) => setDraft({ ...draft, is_active: e.target.value === "active" })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive (hidden from reporting)</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: "1 / 3" }}>
                    <label style={labelStyle}>Investigation *</label>
                    <ServicePicker
                      value={draft.service_ref || null}
                      onChange={(ref) =>
                        setDraft((d) => ({
                          ...d,
                          service_ref: ref,
                          service_names: ref ? [ref.name] : [],
                        }))
                      }
                    />
                    <div style={{ fontSize: 10, color: C.textSub, marginTop: 4 }}>
                      Pick one — that's where radiology reports for this investigation will
                      find and offer this template.
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Description</label>
                    <input
                      style={inputStyle}
                      value={draft.description}
                      onChange={setField("description")}
                      placeholder="Optional note for staff"
                    />
                  </div>
                </div>

                <label style={labelStyle}>Template body</label>
                <ClinicalEditor
                  ref={editorRef}
                  value={draft.content_html}
                  onChange={(html) => setDraft((d) => ({ ...d, content_html: html }))}
                  height={520}
                  placeholder="Write the report format — headings, tables, organ-wise lines, standard normal text…"
                />
                <div style={{ fontSize: 11, color: C.textSub, marginTop: 8 }}>
                  Placeholders like <code>{"{{patient_name}}"}</code> are filled in automatically
                  when a radiologist loads this template onto an order.
                </div>
              </>
            ) : !selected ? (
              <div style={{ padding: 48, textAlign: "center", color: C.textSub, fontSize: 13 }}>
                {loading
                  ? "Loading templates…"
                  : "Select a template on the left, or create a new one."}
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 10,
                    marginBottom: 14,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>
                      {selected.template_name}
                    </div>
                    <div style={{ fontSize: 12, color: C.textSub, marginTop: 3 }}>
                      {selected.modality || "Any modality"}
                      {(selected.service_names || []).length
                        ? ` · ${selected.service_names.join(", ")}`
                        : ""}
                      {selected.description ? ` · ${selected.description}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Btn
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreview(selected)}
                      disabled={hydratingId === selected.template_id}
                    >
                      <SvgIcon name="eye" size={13} color={C.text} />
                      {hydratingId === selected.template_id ? "Loading…" : "Preview"}
                    </Btn>
                    <Btn variant="outline" size="sm" onClick={handleDuplicate}>
                      Duplicate
                    </Btn>
                    <Btn
                      variant="outline"
                      size="sm"
                      onClick={handleEdit}
                      disabled={hydratingId === selected.template_id}
                    >
                      <SvgIcon name="edit" size={13} color={C.text} />
                      {hydratingId === selected.template_id ? "Loading…" : "Edit"}
                    </Btn>
                  </div>
                </div>

                {hydratingId === selected.template_id ? (
                  <div style={{ padding: 24, fontSize: 12, color: C.textSub }}>
                    Loading template content…
                  </div>
                ) : (
                  <ReportContent
                    html={selected.content_html}
                    emptyText="Empty template."
                    style={{
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      padding: 22,
                      background: "#fff",
                      maxHeight: 560,
                      overflowY: "auto",
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {preview ? <PreviewModal template={preview} onClose={() => setPreview(null)} /> : null}
      </div>
    </RadiologyLayout>
  );
}
